import React from 'react';
import Select from 'react-select';
import { TbStack2, TbTruck } from 'react-icons/tb';
import { formatPhone } from '../../Maintenance/Vendors';
import { C, CARD, H2, SUB, BTN_PRIMARY, BTN_SECONDARY, btn, Notice, Field, TextInput, TaskRow, selectStyles, BUSY } from './ui';
import { LIMITS, VERIFY_DELAYS_MS, companyName } from './wizardLogic';

// Config por tipo: Product Line y Vendor comparten el mismo flujo
// (elegir existente o crear → mandar a Sage → confirmar).
const KINDS = {
    productLine: {
        title: 'Product Line',
        Icon: TbStack2,
        noun: 'product line',
        optionLabel: (r) => `${r.code} — ${r.desc}`,
        selectedFields: (s) => [['Code', s.code], ['Description', s.desc]],
        createdTitle: (f) => `${f.code} — ${f.desc}`,
        fields: [
            { id: 'code', label: 'Product Line No (assigned by the system)', col: 'col-12 col-md-4', system: true },
            { id: 'desc', label: 'Description', col: 'col-12 col-md-8', max: LIMITS.productLineDesc },
        ],
    },
    vendor: {
        title: 'Vendor',
        Icon: TbTruck,
        noun: 'vendor',
        optionLabel: (r) => `${r.code} — ${r.name}${r.city ? ` · ${r.city}` : ''}`,
        selectedFields: (s) => [['Vendor No', s.code], ['Name', s.name]],
        createdTitle: (f) => `${f.code} — ${f.name}`,
        fields: [
            { id: 'code', label: 'Vendor No (assigned by the system)', col: 'col-12 col-md-4', system: true },
            { id: 'name', label: 'Vendor name', col: 'col-12 col-md-8', max: LIMITS.vendorName },
            { id: 'address', label: 'Address line 1', col: 'col-12', max: LIMITS.address, autoComplete: 'address-line1' },
            { id: 'city', label: 'City', col: 'col-12 col-md-5', max: LIMITS.city },
            { id: 'state', label: 'State', col: 'col-5 col-md-3', max: 2, upper: true, placeholder: 'TX' },
            { id: 'zip', label: 'Zip code', col: 'col-7 col-md-4', max: 10, inputMode: 'numeric', placeholder: '12345' },
            { id: 'phone', label: 'Telephone', col: 'col-12 col-md-6', type: 'tel', inputMode: 'tel', phone: true, placeholder: '+1 (555) 123-4567' },
            { id: 'email', label: 'Email', col: 'col-12 col-md-6', type: 'email', max: LIMITS.email, inputMode: 'email' },
        ],
    },
};

const segStyle = (active, disabled) => ({
    flex: 1, minHeight: '44px', border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '0.875rem',
    background: active ? '#FFFFFF' : 'transparent', color: active ? C.primary : C.muted,
    boxShadow: active ? '0 1px 3px rgba(15,23,42,0.15)' : 'none', cursor: disabled ? 'not-allowed' : 'pointer',
});

export default function CatalogEntityStep(props) {
    const { kind, idCompany, role, entity, list, locked, busy, errors, numberState, onMode, onSelect, onField, onCreate, onRetry, onNewNumber, onReloadList } = props;
    const cfg = KINDS[kind];
    const Icon = cfg.Icon;
    const task = entity.task;
    const taskBusy = BUSY.indexOf(task.status) >= 0;
    const modeLocked = locked || task.sent || taskBusy;
    const formLocked = locked || task.sent || taskBusy || task.status === 'done';
    const options = (list.rows || []).map((r) => ({ value: r.code, label: cfg.optionLabel(r), row: r }));
    const selectedOpt = entity.selected ? { value: entity.selected.code, label: cfg.optionLabel(entity.selected), row: entity.selected } : null;

    return (
        <div style={CARD}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <Icon style={{ fontSize: '1.4rem', color: C.primary, flexShrink: 0 }} aria-hidden="true" />
                <div>
                    <h2 style={H2}>{cfg.title} · {companyName(idCompany)}</h2>
                    <p style={{ ...SUB, marginTop: 0 }}>{role} company</p>
                </div>
            </div>

            {locked && <Notice kind="info">This step is locked: the item was already sent to Sage with this {cfg.noun}.</Notice>}

            <div role="tablist" aria-label={`${cfg.title} source`} style={{ display: 'flex', gap: '0.25rem', background: '#F1F5F9', borderRadius: '10px', padding: '0.25rem', marginTop: '1rem' }}>
                <button type="button" role="tab" aria-selected={entity.mode === 'existing'} disabled={modeLocked} style={segStyle(entity.mode === 'existing', modeLocked)} onClick={() => onMode('existing')}>
                    Use existing
                </button>
                <button type="button" role="tab" aria-selected={entity.mode === 'new'} disabled={modeLocked} style={segStyle(entity.mode === 'new', modeLocked)} onClick={() => onMode('new')}>
                    Create new
                </button>
            </div>

            {entity.mode === 'existing' ? (
                <div style={{ marginTop: '1rem' }}>
                    <Field id={`${kind}-select`} label={`${cfg.title} in ${companyName(idCompany)}`} required error={errors.selected}>
                        <Select
                            inputId={`${kind}-select`}
                            isSearchable
                            isDisabled={locked || list.loading}
                            isLoading={list.loading}
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                            placeholder={list.loading ? 'Loading…' : `Search ${cfg.noun}s…`}
                            noOptionsMessage={() => `No ${cfg.noun}s found — use "Create new"`}
                            value={selectedOpt}
                            onChange={(opt) => onSelect(opt ? opt.row : null)}
                            options={options}
                        />
                    </Field>
                    {list.error && (
                        <Notice kind="error">
                            {list.error}{' '}
                            <button type="button" style={{ ...BTN_SECONDARY, minHeight: '32px', padding: '0.2rem 0.6rem', marginLeft: '0.25rem' }} onClick={onReloadList}>Reload</button>
                        </Notice>
                    )}
                    {entity.selected && (
                        <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', background: C.infoBg, border: `1px solid ${C.infoLine}`, borderRadius: '10px', padding: '0.75rem 1rem', marginTop: '0.75rem' }}>
                            {cfg.selectedFields(entity.selected).map(([k, v]) => (
                                <div key={k}>
                                    <div style={{ fontSize: '0.7rem', color: C.faint, fontWeight: 700, textTransform: 'uppercase' }}>{k}</div>
                                    <div style={{ fontWeight: 700, color: C.primaryDark }}>{v}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <div style={{ marginTop: '1rem' }}>
                    <div className="row g-3">
                        {cfg.fields.map((f) => (
                            <div className={f.col} key={f.id}>
                                <Field id={`${kind}-${f.id}`} label={f.label} required error={errors[f.id]} hint={f.hint || (f.max && !f.system ? `Max ${f.max} characters` : '')}>
                                    {f.system ? (
                                        <TextInput id={`${kind}-${f.id}`} value={numberState.loading ? 'Assigning…' : entity.form.code} onChange={() => {}} readOnly placeholder="Assigned by the system" />
                                    ) : (
                                        <TextInput
                                            id={`${kind}-${f.id}`}
                                            type={f.type || 'text'}
                                            inputMode={f.inputMode}
                                            autoComplete={f.autoComplete}
                                            placeholder={f.placeholder}
                                            maxLength={f.max}
                                            readOnly={formLocked}
                                            error={errors[f.id]}
                                            value={entity.form[f.id]}
                                            onChange={(v) => onField(f.id, f.phone ? formatPhone(v) : f.upper ? v.toUpperCase().replace(/[^A-Z]/g, '') : v)}
                                        />
                                    )}
                                </Field>
                            </div>
                        ))}
                    </div>
                    {numberState.error && (
                        <Notice kind="error">
                            {numberState.error}{' '}
                            <button type="button" style={{ ...BTN_SECONDARY, minHeight: '32px', padding: '0.2rem 0.6rem' }} onClick={onNewNumber}>Try again</button>
                        </Notice>
                    )}

                    {task.status === 'idle' && (
                        <button type="button" style={{ ...btn(BTN_PRIMARY, busy || numberState.loading || !entity.form.code), width: '100%', marginTop: '1rem' }} disabled={busy || numberState.loading || !entity.form.code} onClick={onCreate}>
                            Create {cfg.noun} in Sage
                        </button>
                    )}

                    {task.status !== 'idle' && (
                        <div style={{ marginTop: '1rem' }}>
                            <TaskRow
                                title={`${cfg.title} in Sage`}
                                detail={cfg.createdTitle(entity.form)}
                                task={task}
                                total={VERIFY_DELAYS_MS.length}
                                onRetry={busy ? null : onRetry}
                                extra={task.status === 'conflict' && !task.sent ? (
                                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                                        <button type="button" style={BTN_SECONDARY} onClick={onNewNumber}>Get a new number</button>
                                    </div>
                                ) : null}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
